import { getFormData } from "helpers/convertFormData";
import { IDebtor, IDeposit, IMyCarrierFilterOption, IPaymentSpeed } from "models/HaulPay";
import { axios } from "../middleware";

//get generic next page API
export const getGenericNextPageAPI = async (link: string, params?: any, signal?: AbortSignal) => {
  return axios.get(link, {
    params: params,
    signal
  });
};

//upload document
export const uploadDocumentAPI = async (params: File, config: any) => {
  return axios.patch("/factoring/profiles/", getFormData({ attachments: params }), config);
};

//search company
export const searchCompanyAPI = async (
  dba?: string,
  factoring?: string,
  data?: any,
  limit: number = 20
) => {
  return axios.get(`/factoring/debtors/`, {
    params: { dba, factoring, ...data, limit }
  });
};

//search relationship
export const searchRelationshipAPI = async (company_name?: string) => {
  return axios.get(`/factoring/debtors/relationship/?dba=&mc=&dot=&tax_id=&factoring=`, {
    params: { company_name, limit: 20 }
  });
};

//search company by mc or dot
export const getCarrierPaymentProfilesAPI = async (type: string, value: string) => {
  return axios.get(`/factoring/payment_profiles/all/`, {
    params: { limit: 20, status: "approved,needs_review,pending", [type]: value }
  });
};

export const getPaymentProfileRelationAPI = async (factoring_id: string, carrier_id: string) => {
  return axios.get(`/factoring/${factoring_id}/payment_profiles/${carrier_id}/`);
};

//search carrier for funding request
export const getCarrierForFRAPI = async (factoring_id: string, value: string) => {
  return axios.get(`/factoring/${factoring_id}/payment_profiles/`, {
    params: { status: "approved,needs_review,pending,declined", carrier_dba: value }
  });
};

// Get Available Groups for Broker
export const getCarrierGroupsAPI = async (limit = 20) => {
  return axios.get(`/groups`, {
    params: { limit: limit, ordering: "-created" }
  });
};

// Get Invited Carriers
export const getInvitedCarriersAPI = async (group_id: string, limit = 20, offset = 0) => {
  return axios.get(`/groups/${group_id}/invited`, {
    params: { limit: limit, deleted: true, offset: offset }
  });
};

// Check matched ic
export const getMatchedEmailCarriersAPI = async (data: { [key in string]: string }) => {
  return axios.get(`/factoring/payment_profiles/all/`, {
    params: {
      limit: 20,
      ...data
    }
  });
};

export const createPaymentProfileAPI = async (data: any) => {
  return axios.post("/factoring/payment_profiles/", data);
};

// Send carrier invitation by email
export const sendCarrierInvitationAPI = async (group_id: string, email = "", phone_number = "") => {
  return axios.post(`/groups/${group_id}/invite/`, {
    emails: email ? [email] : [],
    phone_numbers: phone_number ? [phone_number] : []
  });
};

// Get My Carriers
export const getFactoringPaymentProfilesAPI = async (
  factoring_id: string,
  filter_data?: IMyCarrierFilterOption,
  limit?: number,
  offset?: number
) => {
  let params = {
    offset,
    limit
  };
  if (filter_data) {
    params = {
      ...params,
      [filter_data.filter_type]:
        filter_data.filter_type === "status" ? filter_data.filter_status : filter_data.filter_text
    };
  }
  return axios.get(`/factoring/${factoring_id}/payment_profiles`, {
    params: params
  });
};

// Connect payment profile to broker
export const connectPaymentProfileAPI = async (
  factoring_id: string,
  payment_profile_id: string
) => {
  return axios.post(`/factoring/${factoring_id}/payment_profiles/`, {
    factoring_id,
    payment_profile_id
  });
};

// Get Default Payment Settings
export const getDefaultPaymentTermsAPI = async (factoring_id: string) => {
  return axios.get(`/factoring/${factoring_id}/terms`);
};

// Get factoring company list
export const getFactoringCompanyAPI = async (name: string) => {
  return axios.get(`/factoring/company/`, {
    params: {
      name,
      status: "approved, pending"
    }
  });
};

// Calculate funding request values
export const calculateFR = async (params: {
  apply_reserve_holdback?: boolean;
  debtor?: string;
  factored?: boolean;
  invoice_amount?: number;
  bill_out_only?: boolean;
  contract_type?: string;
  create_rates_revision?: boolean;
  factoring_fee_split_rate?: number;
  payment_profile?: string;
  payment_profile_amount?: number;
  payment_profile_invoice_amount?: number;
  payment_speed?: number;
  self_finance_requested?: boolean;
  discount_rate_comfreight?: number;
  funding_request_id?: string;
  factoring?: string;
}) => {
  return axios.post("/funding_requests/calculations/", params);
};

export const updateInvitedUserAPI = async (user_id: string, data: any) => {
  return axios.patch(`/groups/invited_user/${user_id}/`, data);
};

export const setFactoringCompanyRelationAPI = async (
  company_id: string,
  broker_client: string,
  payment_profile: string
) => {
  return axios.post(`/factoring/company/${company_id}/relations/`, {
    broker_client,
    payment_profile
  });
};

export const sendInviteToFactoringCompanyAPI = async (
  company_id: string,
  payment_profile: string
) => {
  return axios.post(`/factoring/company/${company_id}/send_invite/`, {
    payment_profile
  });
};

// Update Default Payment setting
export const updateDefaultPaymentTermAPI = async (
  factoring_id: string,
  id: string,
  data: IPaymentSpeed
) => {
  return axios.patch(`/factoring/${factoring_id}/terms/${id}`, data);
};

export const updateCarrierProfileAPI = async (payload: {
  factoring_id: string;
  carrier_id: string;
  send_update_email?: boolean;
  data: {
    factoring_fee_split_rate?: number;
    enable_discount?: boolean;
    payout_days?: string;
    external_code?: string;
    factoring_company_relation_id?: string | null;
  };
}) => {
  return axios.patch(
    `/factoring/${payload.factoring_id}/payment_profiles/${payload.carrier_id}/`,
    payload.data,
    {
      params: {
        send_update_email: payload.send_update_email
      }
    }
  );
};

//get pending transactions
export const getPendingTransactionAPI = async (
  params?: Record<string, any>,
  signal?: AbortSignal
) => {
  const queryString = params ? `?${new URLSearchParams(params).toString()}` : "";
  const response = await axios.get(`/cmftstripe/pending_transactions/${queryString}`, { signal });
  return response;
};

//get history transactions
export const getHistoryTransactionAPI = async (params: any = {}) => {
  return axios.get("/cmftstripe/transactions/", { params });
};

export const stripeRegulatoryReceipt = async (obligationId: string) => {
  const url = `/cmftstripe/regulatory_receipt/`;
  const options = {
    url,
    method: "GET",
    params: { obligation_id: obligationId }
  };
  return axios(options);
};

//add new customer profile
export const addNewCustomerProfileAPI = async (params: any) => {
  return axios.post("/factoring/debtors/add/", params);
};

export const attachFactoringDocumentAPI = async (
  factoring_id: string,
  data: { category: string; filename: string }
) => {
  return axios.post(`factoring/${factoring_id}/attachments/`, data);
};

export const uploadFactoringDocumentAPI = async (url: string, params: any, config: any) => {
  return axios.post(url, getFormData({ ...params }), config);
};

// my customers
export const myCustomersAPI = async (params: IDebtor) => {
  return axios.get(
    `factoring/debtors/relationship/?mc=${params?.mc ?? ""}&dot=${params?.dot ?? ""}&tax_id=${params?.tax_id ?? ""}&company_name=${params?.company_name ?? ""}&limit=${params.limit}&ordering=-time_posted&offset=${params.offset}`
  );
};

// factoring debtors
export const factoringDebtorAPI = async (params: IDebtor) => {
  return axios.get(
    `/factoring/debtors/?dba=${params?.dba ?? ""}&mc=${params?.mc ?? ""}&dot=${params?.dot ?? ""}&tax_id=${params?.tax_id ?? ""}&factoring=${params?.factoring ?? ""}&limit=${params.limit ?? ""}&company_name=${params?.company_name ?? ""}`
  );
};

// add customer
export const addCustomerAPI = async (params: any) => {
  return axios.patch("/factoring/debtors/add/", params);
};

// save credit
export const saveCreditAPI = async (params: any) => {
  return axios.patch(`/factoring/debtors/relationship/${params.id}/retrieve/`, params);
};

// request increase
export const requestIncreaseAPI = async (params: any) => {
  return axios.post(`/factoring/debtors/relationship/${params.id}/credit_increase/`, params);
};

// send Noa Request
export const sendNoaAPI = async (params: any) => {
  return axios.post("/factoring/debtors/relationship/", params);
};

export const getFactoringUserProfileAPI = async () => {
  return axios.get("factoring/profiles/");
};

export const calculateFRAPI = async (data: {
  debtor: string;
  factored: boolean;
  invoice_amount: number;
  factoring?: string;
  apply_reserve_holdback?: boolean;
  bill_out_only?: boolean;
  contract_type?: string;
  create_rates_revision?: boolean;
  discount_rate_comfreight?: number;
  factoring_fee_split_rate?: number;
  funding_request_id?: string;
  payment_profile?: string;
  payment_profile_amount?: number;
  payment_profile_invoice_amount?: number;
  payment_speed?: number;
  self_finance_requested?: boolean;
}) => {
  return axios.post("funding_requests/calculations/", data);
};

// history list
export const historyListAPI = async () => {
  return axios.get("/factoring/funding/request/?limit=20&ordering=-created");
};

// funding request details
export const historyDetailsAPI = async (id: string) => {
  return axios.get(`/factoring/funding/request/${id}`);
};

export const totalRevenueAPI = async () => {
  return axios.get("/factoring/funding/request/calculations/");
};

// assigned loads
export const assignedLoadsAPI = async (status?: string, ordering = "-created") => {
  let params: any = {
    limit: 20,
    ordering: ordering,
    assigned_loads: true
  };
  if (status) params = { ...params, status };
  return axios.get("/factoring/funding/request/", {
    params: params
  });
};

// make deposit
export const makeDepositAPI = async (data: IDeposit) => {
  return axios.post("/cmftstripe/deposit/", data);
};

// send invoice
export const sendInvoiceAPI = async (id: string) => {
  return axios.patch(`/funding_requests/${id}/send_invoice/`);
};

// HP-8868: broker resend of the carrier rate confirmation after editing the carrier pay amount
export const resendRateConfirmationAPI = async (id: string) => {
  return axios.patch(`/funding_requests/${id}/resend_rate_confirmation/`);
};

// delete funding request
export const deleteFundingRequestAPI = async (id: string) => {
  return axios.delete(`/factoring/funding/request/${id}/`);
};

// broker self-service decline of an approved self-finance FR (cancels all obligations + closes the receivable)
export const declineSelfFinanceFRAPI = async (id: string) => {
  return axios.post(`/funding_requests/${id}/decline/`);
};

// broker self-service cancel of the carrier-funding leg on an approved off-platform self-finance FR
// (FR stays Approved, receivable stays open)
export const cancelCarrierPaymentAPI = async (id: string) => {
  return axios.post(`/funding_requests/${id}/cancel_carrier_payment/`);
};

// delete fr attachment
export const deleteFRAttachmentAPI = async (fundingRequestId: string, fileId: string) => {
  return axios.delete(`/factoring/funding/request/${fundingRequestId}`, {
    params: {
      file_id: fileId
    }
  });
};

// get invoice for fr
export const getInvoiceForFRAPI = async (fundingRequestId: string, value: string) => {
  return axios.get(`/factoring/funding/request/${fundingRequestId}/files`, {
    params: { document: value }
  });
};

// upload funding request docs
export const uploadFRDocsAPI = async (file: File) => {
  return axios.post("/funding_requests/bulk_upload/", getFormData({ attachment: file }));
};

export const getWalletOnboardingUrlAPI = async () => {
  return axios.post("/cmftstripe/onboarding/");
};

export const fetchStripeAccount = async () => {
  const url = `/cmftstripe/account/`;
  const options = {
    url,
    method: "GET"
  };
  return axios(options);
};

export const stripeAddBankAccount = async (companyId: string) => {
  const options = {
    url: `/cmftstripe/add_bank_account/`,
    method: "POST",
    data: {
      company_id: companyId
    }
  };
  return axios(options);
};

export const stripeAddExternalBankAccount = async (paymentManagerId: string, accountId: string) => {
  const options = {
    url: `/cmftstripe/accounts/${accountId}`,
    method: "PATCH",
    data: {
      stripe_external_bank_account_id: paymentManagerId
    }
  };
  return axios(options);
};

export const confirmMicroDepositBankAccount = async (code: string) => {
  const response = await axios({
    url: `/cmftstripe/micro_deposit/`,
    method: "POST",
    data: { code }
  });
  return response.data;
};
export const getReportURL = async (params: any) => {
  return axios.post("/funding_requests/reports/broker/", params);
};

export const getReportJob = async (reportFileId: string) => {
  return axios.get(`/reports/jobs/${reportFileId}/`);
};

export const getCarrierReport = async (params: any) => {
  return axios.post("/factoring/funding/request/reports/", params);
};

export const factoringDebtorRelation = async (data: any) => {
  const options = {
    url: `/factoring/debtors/relationship/`,
    method: "POST",
    data
  };
  return axios(options);
};

export const completeFundingRequestAPI = (
  fr_id: string,
  attachments: { category: string; filename: string; visible_to: string[] }[]
) => {
  return axios.patch(
    `/factoring/funding/request/${fr_id}/`,
    {
      status: "special_pending",
      attachments
    },
    {
      params: {
        details: "extended"
      }
    }
  );
};

export const createFRLineItem = async (data: any) => {
  const options = {
    url: `/funding_requests/${data.fundingRequestId}/items/`,
    method: "POST",
    data
  };
  return axios(options);
};

export const updateFRLineItem = async (data: any) => {
  const options = {
    url: `/funding_requests/${data?.fundingRequestId}/items/${data?.lineItemId}/`,
    method: "PATCH",
    data
  };
  return axios(options);
};

export const searchPoNumberAPI = async (poNumber: string, id = "") => {
  return axios.get(`/factoring/purchase_order_number/check/`, {
    params: {
      purchase_order_number: poNumber,
      funding_request_id: id
    }
  });
};

export const getBrokerGroupCarriers = (factoring_id: string, filters?: any) => {
  return axios.get(`/factoring/${factoring_id}/payment_profiles/`, {
    params: filters
  });
};

export async function fetchProductCatalog<T>(): Promise<T> {
  const response = await axios.get<T>("/cmftstripe/subscriptions/");
  return response.data;
}

export async function updateAttachment<T>({
  attachment,
  fundingRequestId,
  attachmentId
}: {
  attachment: T;
  fundingRequestId: string;
  attachmentId: string;
}): Promise<T> {
  const url = `/funding_requests/${fundingRequestId}/attachments/${attachmentId}/`;
  const response = await axios.patch<T>(url, attachment);
  return response.data;
}
